import React from 'react';
import { Eye, Gavel, PauseCircle, PlayCircle, RefreshCw, RotateCcw } from 'lucide-react';
import { archiveService } from '../../services/archiveService';
import type { ArchiveRecordFilters, ArchiveActionPayload } from '../../services/archiveService';
import type { ArchiveBatch, ArchiveBatchItem, ArchiveDocument, ArchiveRecord, ArchiveRecordType } from '../../types/domain';
import { permissionService } from '../../services/permissionService';
import { DetailDrawer, EmptyTableState, FormModal, RecordsTableCard, StatusBadge } from '../records';
import DismissibleError from '../DismissibleError';
import './ArchiveManagement.css';

const recordTypeOptions: Array<[ArchiveRecordType, string]> = [
  ['firearm', 'Firearm'],
  ['firearm_issuance', 'Firearm Issuance'],
  ['multi_issuance_case', 'Multi-Issuance Case'],
  ['firearm_incident', 'Firearm Incident'],
  ['firearm_repair', 'Firearm Repair'],
];

const automaticRecordTypes: ArchiveRecordType[] = [
  'firearm_issuance',
  'multi_issuance_case',
  'firearm_incident',
  'firearm_repair',
];

const formatDateTime = (input?: string | null): string => {
  if (!input) return '-';
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? input : date.toLocaleString();
};

const snapshotPreview = (snapshot: Record<string, any>): string =>
  JSON.stringify(snapshot || {}, null, 2);

const snapshotValue = (snapshot: Record<string, any>, keys: string[]): any => {
  for (const key of keys) {
    const value = snapshot?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '-';
};

const formatValue = (value: any): string => {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const selectedTypeLabels = automaticRecordTypes
  .map((type) => recordTypeOptions.find(([value]) => value === type)?.[1] || type)
  .join(', ');

const ArchiveSection: React.FC<{ title: string; rows: Array<[string, any]> }> = ({ title, rows }) => (
  <section className="archive-detail-section">
    <h3>{title}</h3>
    <dl>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{formatValue(value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  </section>
);

const ArchiveManagement: React.FC = () => {
  const [tab, setTab] = React.useState<'records' | 'batches'>('records');
  const [records, setRecords] = React.useState<ArchiveRecord[]>([]);
  const [batches, setBatches] = React.useState<ArchiveBatch[]>([]);
  const [batchItems, setBatchItems] = React.useState<ArchiveBatchItem[]>([]);
  const [recordDocuments, setRecordDocuments] = React.useState<ArchiveDocument[]>([]);
  const [selectedRecord, setSelectedRecord] = React.useState<ArchiveRecord | null>(null);
  const [selectedBatch, setSelectedBatch] = React.useState<ArchiveBatch | null>(null);
  const [filters, setFilters] = React.useState<ArchiveRecordFilters>({ is_archived: true });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [reasonAction, setReasonAction] = React.useState<{
    type: 'restore' | 'hold' | 'release';
    record: ArchiveRecord;
  } | null>(null);
  const [reason, setReason] = React.useState('');
  const [batchMode, setBatchMode] = React.useState<'dry_run' | 'execute'>('dry_run');
  const [batchLimit, setBatchLimit] = React.useState('100');
  const [executeConfirmOpen, setExecuteConfirmOpen] = React.useState(false);
  const [executeAcknowledged, setExecuteAcknowledged] = React.useState(false);
  const [batchRunning, setBatchRunning] = React.useState(false);
  const batchRunningRef = React.useRef(false);

  const canRestore = permissionService.hasPermission('records.archive.restore');
  const canHold = permissionService.hasPermission('records.archive.legal_hold');
  const canRun = permissionService.hasPermission('records.archive.run');

  const loadRecords = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRecords(await archiveService.getArchiveRecords(filters));
    } catch (err: any) {
      setError(err?.message || 'Archive records could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadBatches = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setBatches(await archiveService.getArchiveBatches());
    } catch (err: any) {
      setError(err?.message || 'Archive batches could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (tab === 'records') loadRecords();
    else loadBatches();
  }, [loadBatches, loadRecords, tab]);

  const updateFilter = (key: keyof ArchiveRecordFilters, value: string) => {
    setFilters((current) => ({
      ...current,
      [key]: value === '' ? undefined : key === 'is_archived' || key === 'is_legal_hold' ? value === 'true' : value,
    }));
  };

  const openBatch = async (batch: ArchiveBatch) => {
    setSelectedBatch(batch);
    setBatchItems([]);
    try {
      setBatchItems(await archiveService.getArchiveBatchItems(batch.id));
    } catch (err: any) {
      setError(err?.message || 'Archive batch items could not be loaded.');
    }
  };

  const openRecord = async (record: ArchiveRecord) => {
    setSelectedRecord(record);
    setRecordDocuments([]);
    try {
      setRecordDocuments(await archiveService.getArchiveRecordDocuments(record.id));
    } catch (err: any) {
      setError(err?.message || 'Archive documents could not be loaded.');
    }
  };

  const submitReasonAction = async () => {
    if (!reasonAction || !reason.trim()) return;
    const { type, record } = reasonAction;
    const payload: ArchiveActionPayload = {
      record_type: record.record_type,
      record_id: record.record_id,
      reason,
    };
    setError('');
    try {
      if (type === 'restore') await archiveService.restoreRecord(record.id, reason);
      if (type === 'hold') await archiveService.applyLegalHold(payload);
      if (type === 'release') await archiveService.releaseLegalHold(record.id, reason);
      setReasonAction(null);
      setReason('');
      await loadRecords();
    } catch (err: any) {
      setError(err?.message || 'Archive action could not be completed.');
    }
  };

  const runBatch = async (modeOverride?: 'dry_run' | 'execute') => {
    if (batchRunningRef.current) return;
    batchRunningRef.current = true;
    setError('');
    setBatchRunning(true);
    try {
      const limit = Number(batchLimit);
      const mode = modeOverride || batchMode;
      const payload = {
        record_types: automaticRecordTypes,
        limit: Number.isFinite(limit) && limit > 0 ? limit : undefined,
        ...(mode === 'execute' ? { confirm_execute: true } : {}),
      };
      const batch = mode === 'execute'
        ? await archiveService.executeBatch(payload)
        : await archiveService.dryRunBatch(payload);
      setExecuteConfirmOpen(false);
      setExecuteAcknowledged(false);
      await loadBatches();
      await openBatch(batch);
    } catch (err: any) {
      setError(err?.message || 'Archive batch could not be started.');
    } finally {
      batchRunningRef.current = false;
      setBatchRunning(false);
    }
  };

  const handleBatchClick = () => {
    if (batchMode === 'execute') {
      setExecuteAcknowledged(false);
      setExecuteConfirmOpen(true);
      return;
    }
    runBatch('dry_run');
  };

  return (
    <div className="admin-page audit-logs-page">
      <div className="page-header">
        <div>
          <h1>Archived Records</h1>
          <p>Review retained workflow records, Legal Hold state, restores, and archive batches.</p>
        </div>
        <div className="segmented-tabs" role="tablist" aria-label="Archive workspace">
          <button className={tab === 'records' ? 'active' : ''} type="button" onClick={() => setTab('records')}>Records</button>
          <button className={tab === 'batches' ? 'active' : ''} type="button" onClick={() => setTab('batches')}>Batches</button>
        </div>
      </div>

      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}

      {tab === 'records' ? (
        <RecordsTableCard
          title="Archive Records"
          meta={loading ? 'Loading' : `${records.length} shown`}
          actions={<button className="icon-button" type="button" aria-label="Refresh archive records" onClick={loadRecords}><RefreshCw size={17} /></button>}
          toolbar={(
            <div className="archive-filter-grid">
              <label>Record Type<select value={filters.record_type || ''} onChange={(event) => updateFilter('record_type', event.target.value)}>
                <option value="">All types</option>
                {recordTypeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select></label>
              <label>Archived<select value={filters.is_archived === undefined ? '' : String(filters.is_archived)} onChange={(event) => updateFilter('is_archived', event.target.value)}>
                <option value="">Any</option><option value="true">Archived</option><option value="false">Active</option>
              </select></label>
              <label>Legal Hold<select value={filters.is_legal_hold === undefined ? '' : String(filters.is_legal_hold)} onChange={(event) => updateFilter('is_legal_hold', event.target.value)}>
                <option value="">Any</option><option value="true">On Hold</option><option value="false">Not Held</option>
              </select></label>
              <label>Archived After<input type="date" value={filters.archived_after || ''} onChange={(event) => updateFilter('archived_after', event.target.value)} /></label>
              <label>Policy<input value={filters.policy_code || ''} onChange={(event) => updateFilter('policy_code', event.target.value)} /></label>
              <label>Search<input value={filters.search || ''} onChange={(event) => updateFilter('search', event.target.value)} /></label>
            </div>
          )}
        >
          <table>
            <thead><tr><th>Reference</th><th>Type</th><th>Department</th><th>Original Status</th><th>Archived</th><th>Policy</th><th>Legal Hold</th><th>Actions</th></tr></thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td>{record.reference}</td>
                  <td>{record.record_type_display}</td>
                  <td>{record.department_name || '-'}</td>
                  <td><StatusBadge status={record.original_status || '-'} /></td>
                  <td>{formatDateTime(record.archived_at)}<br /><small>{record.archived_by_username || '-'}</small></td>
                  <td>{record.archive_policy_code || '-'}</td>
                  <td><StatusBadge status={record.is_legal_hold ? 'legal hold' : 'clear'} /></td>
                  <td className="table-actions">
                    <button className="icon-button" type="button" aria-label="View archive record" onClick={() => openRecord(record)}><Eye size={16} /></button>
                    {canRestore && record.is_archived && <button className="icon-button" type="button" aria-label="Restore archive record" onClick={() => setReasonAction({ type: 'restore', record })}><RotateCcw size={16} /></button>}
                    {canHold && !record.is_legal_hold && <button className="icon-button" type="button" aria-label="Apply Legal Hold" onClick={() => setReasonAction({ type: 'hold', record })}><PauseCircle size={16} /></button>}
                    {canHold && record.is_legal_hold && <button className="icon-button" type="button" aria-label="Release Legal Hold" onClick={() => setReasonAction({ type: 'release', record })}><Gavel size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!records.length && !loading && <EmptyTableState title="No archive records found" />}
        </RecordsTableCard>
      ) : (
        <RecordsTableCard
          title="Archive Batches"
          meta={loading ? 'Loading' : `${batches.length} shown`}
          actions={<button className="icon-button" type="button" aria-label="Refresh archive batches" onClick={loadBatches}><RefreshCw size={17} /></button>}
          toolbar={canRun ? (
            <div className="archive-filter-grid">
              <label>Mode<select disabled={batchRunning} value={batchMode} onChange={(event) => setBatchMode(event.target.value as 'dry_run' | 'execute')}>
                <option value="dry_run">Dry Run</option><option value="execute">Execute</option>
              </select></label>
              <label>Limit<input disabled={batchRunning} type="number" min="1" value={batchLimit} onChange={(event) => setBatchLimit(event.target.value)} /></label>
              <button className="btn btn-primary" type="button" disabled={batchRunning} onClick={handleBatchClick}><PlayCircle size={16} /> {batchRunning ? 'Running...' : 'Run Batch'}</button>
            </div>
          ) : null}
        >
          <table>
            <thead><tr><th>ID</th><th>Mode</th><th>Status</th><th>Evaluated</th><th>Archived</th><th>Skipped</th><th>Failed</th><th>Started</th><th>Actions</th></tr></thead>
            <tbody>{batches.map((batch) => (
              <tr key={batch.id}>
                <td>#{batch.id}</td><td>{batch.mode}</td><td><StatusBadge status={batch.status} /></td>
                <td>{batch.candidate_count}</td><td>{batch.archived_count}</td><td>{batch.skipped_count}</td><td>{batch.failed_count}</td>
                <td>{formatDateTime(batch.started_at)}</td>
                <td><button className="icon-button" type="button" aria-label="View archive batch" onClick={() => openBatch(batch)}><Eye size={16} /></button></td>
              </tr>
            ))}</tbody>
          </table>
          {!batches.length && !loading && <EmptyTableState title="No archive batches found" />}
        </RecordsTableCard>
      )}

      <DetailDrawer open={Boolean(selectedRecord)} title={selectedRecord?.reference || 'Archive Record'} subtitle={selectedRecord?.record_type_display} onClose={() => setSelectedRecord(null)}>
        {selectedRecord && (
          <div className="archive-detail-stack">
            <ArchiveSection title="Archive Metadata" rows={[
              ['Department', selectedRecord.department_name],
              ['Archived', formatDateTime(selectedRecord.archived_at)],
              ['Archived By', selectedRecord.archived_by_username],
              ['Policy', selectedRecord.archive_policy_code],
              ['Retention Until', selectedRecord.retention_until],
              ['Archive Count', selectedRecord.archive_count],
              ['Reason', selectedRecord.archive_reason],
            ]} />
            <ArchiveSection title="Original Status" rows={[
              ['Status', selectedRecord.original_status],
              ['Reference', selectedRecord.reference],
              ['Record ID', selectedRecord.record_id],
            ]} />
            <ArchiveSection title="Firearm And Officer" rows={[
              ['Firearm', snapshotValue(selectedRecord.archived_snapshot, ['firearm_serial', 'serial_number', 'firearm'])],
              ['Officer', snapshotValue(selectedRecord.archived_snapshot, ['officer_name', 'accountable_officer', 'officer'])],
              ['Station/Unit', snapshotValue(selectedRecord.archived_snapshot, ['station_unit', 'target_station_unit', 'unit'])],
            ]} />
            <ArchiveSection title="Documents And Evidence" rows={[
              ['Form 18', snapshotValue(selectedRecord.archived_snapshot, ['form18_reference', 'form18_id', 'form18'])],
              ['ARE', snapshotValue(selectedRecord.archived_snapshot, ['are_reference', 'are_id', 'are'])],
              ['Evidence Count', snapshotValue(selectedRecord.archived_snapshot, ['evidence_count'])],
            ]} />
            <section className="archive-detail-section">
              <h3>Retained Files</h3>
              {recordDocuments.length ? (
                <ul className="archive-document-list">
                  {recordDocuments.map((document) => (
                    <li key={`${document.document_type}-${document.document_id}`}>
                      <a href={document.view_url} target="_blank" rel="noreferrer">{document.filename}</a>
                      <small>{document.document_type} · {document.mime_type} · {document.file_size || '-'} bytes</small>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="archive-empty-note">No retained files found.</p>
              )}
            </section>
            <ArchiveSection title="Related Workflows" rows={[
              ['Issuance', snapshotValue(selectedRecord.archived_snapshot, ['issuance_id'])],
              ['Return', snapshotValue(selectedRecord.archived_snapshot, ['return_record_id'])],
              ['Repair', snapshotValue(selectedRecord.archived_snapshot, ['repair_id'])],
              ['Incident', snapshotValue(selectedRecord.archived_snapshot, ['incident_id'])],
            ]} />
            <ArchiveSection title="Archive Event History" rows={[
              ['Restored', formatDateTime(selectedRecord.restored_at)],
              ['Restored By', selectedRecord.restored_by_username],
              ['Restore Count', selectedRecord.restore_count],
              ['Latest Restore Reason', selectedRecord.restore_reason],
            ]} />
            <ArchiveSection title="Legal Hold History" rows={[
              ['On Legal Hold', selectedRecord.is_legal_hold],
              ['Applied', formatDateTime(selectedRecord.legal_hold_applied_at)],
              ['Applied By', selectedRecord.legal_hold_applied_by_username],
              ['Reason', selectedRecord.legal_hold_reason],
              ['Released', formatDateTime(selectedRecord.legal_hold_released_at)],
              ['Release Reason', selectedRecord.legal_hold_release_reason],
            ]} />
            <details className="archive-raw-snapshot">
              <summary>Raw Snapshot</summary>
              <pre className="json-preview">{snapshotPreview(selectedRecord.archived_snapshot)}</pre>
            </details>
          </div>
        )}
      </DetailDrawer>

      <DetailDrawer open={Boolean(selectedBatch)} title={selectedBatch ? `Archive Batch #${selectedBatch.id}` : 'Archive Batch'} subtitle={selectedBatch?.mode} onClose={() => setSelectedBatch(null)}>
        {selectedBatch?.summary?.quota_consumed_count !== undefined ? (
          <p>
            Quota used: {selectedBatch.summary.quota_consumed_count} / {selectedBatch.summary.limit ?? 'Unlimited'}
          </p>
        ) : null}
        <table>
          <thead><tr><th>Record</th><th>Department</th><th>Result</th><th>Reason</th></tr></thead>
          <tbody>{batchItems.map((item) => <tr key={item.id}><td>{item.record_type} #{item.record_id}</td><td>{item.department_name || '-'}</td><td><StatusBadge status={item.result} /></td><td>{item.reason_code || '-'}</td></tr>)}</tbody>
        </table>
        {!batchItems.length && <EmptyTableState title="No batch items found" />}
      </DetailDrawer>

      <FormModal
        open={executeConfirmOpen}
        title="Execute Archive Batch"
        onClose={() => { if (!batchRunning) setExecuteConfirmOpen(false); }}
        footer={<><button className="btn btn-secondary" type="button" disabled={batchRunning} onClick={() => setExecuteConfirmOpen(false)}>Cancel</button><button className="btn btn-primary" type="button" disabled={!executeAcknowledged || batchRunning} onClick={() => runBatch('execute')}><PlayCircle size={16} /> {batchRunning ? 'Executing...' : 'Execute Archive Batch'}</button></>}
      >
        <div className="archive-execute-warning">
          <p>This is not a dry run.</p>
          <p>Eligible records will be removed from active operational views.</p>
          <label>
            <input
              type="checkbox"
              checked={executeAcknowledged}
              disabled={batchRunning}
              onChange={(event) => setExecuteAcknowledged(event.target.checked)}
            />
            I understand that Execute removes eligible records from active operational views and retains them in Archived Records.
          </label>
          <dl>
            <dt>Selected record types</dt>
            <dd>{selectedTypeLabels}</dd>
            <dt>Maximum records</dt>
            <dd>{Number(batchLimit) > 0 ? batchLimit : 'Default'}</dd>
          </dl>
        </div>
      </FormModal>

      <FormModal
        open={Boolean(reasonAction)}
        title={reasonAction?.type === 'restore' ? 'Restore Record' : reasonAction?.type === 'hold' ? 'Apply Legal Hold' : 'Release Legal Hold'}
        onClose={() => { setReasonAction(null); setReason(''); }}
        footer={<><button className="btn btn-secondary" type="button" onClick={() => { setReasonAction(null); setReason(''); }}>Cancel</button><button className="btn btn-primary" type="button" onClick={submitReasonAction} disabled={!reason.trim()}>Submit</button></>}
      >
        <label className="archive-reason-field">Reason<textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={5} /></label>
      </FormModal>
    </div>
  );
};

export default ArchiveManagement;
